import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../hooks/usePermissions';
import { approvalRequestsAPI, contributionsAPI, peoplePlatformAPI, workflowPlatformAPI } from '@/services';
import { mapPeopleListResponse } from '../models/memberModel';
import { useToast } from '../components/Toast';
import SearchAndFilterBar from '../components/SearchAndFilterBar';
import Pagination from '../components/Pagination';
import PageHeader from '../components/PageHeader';
import ModalShell from '../components/ModalShell';

const ApprovalRequests = ({ embedded = false }) => {
  const { t, i18n } = useTranslation('common');
  const localeFmt = (i18n.resolvedLanguage || i18n.language || 'fr').startsWith('en') ? 'en-US' : 'fr-FR';
  const { hasPermission } = usePermissions();
  const { success, error } = useToast();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState({});
  const [requestsPage, setRequestsPage] = useState(1);
  const [requestsTotal, setRequestsTotal] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [approvalTab, setApprovalTab] = useState('entrea');
  const [workflowLookupId, setWorkflowLookupId] = useState('');
  const [workflowDetail, setWorkflowDetail] = useState(null);
  const [workflowHistory, setWorkflowHistory] = useState([]);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [reasonModal, setReasonModal] = useState({
    open: false,
    mode: null,
    targetId: '',
    title: '',
    label: '',
    placeholder: '',
    required: false,
  });
  const [reasonInput, setReasonInput] = useState('');
  const [reasonSubmitting, setReasonSubmitting] = useState(false);
  const [wfCreate, setWfCreate] = useState({
    workflow_type: 'generic',
    entity_id: '',
    entity_type: 'resource',
    initial_status: 'pending',
  });

  useEffect(() => {
    const canApprove = hasPermission('contributions.approve');
    if (!canApprove) {
      setLoading(false);
      return;
    }
    loadData();
    // Chargement initial uniquement
  }, [requestsPage, itemsPerPage]);

  useEffect(() => {
    filterRequests();
  }, [requests, searchQuery, selectedStatus, members, t]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {
        limit: itemsPerPage,
        skip: (requestsPage - 1) * itemsPerPage
      };
      const [requestsRes, membersRes] = await Promise.all([
        approvalRequestsAPI.getAll(params),
        peoplePlatformAPI.list({ limit: 2000, skip: 0 })
      ]);
      
      // Gérer les deux formats de réponse
      let requestsList = [];
      if (Array.isArray(requestsRes?.data)) {
        requestsList = requestsRes.data;
        setRequestsTotal(requestsRes.data.length);
      } else {
        requestsList = requestsRes.data.requests || [];
        setRequestsTotal(requestsRes.data.total || 0);
      }
      setRequests(requestsList);
      setFilteredRequests(requestsList);
      
      // Créer un dictionnaire de membres pour accès rapide
      const membersDict = {};
      const membersList = mapPeopleListResponse(membersRes?.data);
      membersList.forEach(member => {
        if (member?.id) {
          membersDict[member.id] = member;
        }
      });
      setMembers(membersDict);
    } catch (err) {
      console.error('Erreur lors du chargement des demandes:', err);
      const errorDetail = err.response?.data?.detail;
      const errorMessage = errorDetail || err.message || t('approvalRequests.loadError');
      error(errorMessage);
      // Réinitialiser les états en cas d'erreur
      setRequests([]);
      setFilteredRequests([]);
      setRequestsTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (request) => {
    try {
      const requestId = String(request?.id || '').trim();
      if (!requestId) {
        error(t('approvalRequests.invalidRequest'));
        return;
      }
      if (request?.resource_type === 'contribution') {
        await contributionsAPI.approve(requestId);
        success(t('approvalRequests.successContributionApproved'));
      } else {
        await approvalRequestsAPI.approve(requestId);
        success(t('approvalRequests.successNotificationRead'));
      }
      loadData();
    } catch (err) {
      error(err.response?.data?.detail || t('approvalRequests.approveError'));
    }
  };

  const openReasonModal = ({ mode, targetId, title, label, placeholder, required = false }) => {
    setReasonInput('');
    setReasonModal({
      open: true,
      mode,
      targetId: String(targetId || ''),
      title,
      label,
      placeholder,
      required,
    });
  };

  const closeReasonModal = () => {
    if (reasonSubmitting) return;
    setReasonModal({
      open: false,
      mode: null,
      targetId: '',
      title: '',
      label: '',
      placeholder: '',
      required: false,
    });
    setReasonInput('');
  };

  const handleReject = async (request, reasonText) => {
    try {
      const requestId = String(request?.id || '').trim();
      if (!requestId) {
        error(t('approvalRequests.invalidRequest'));
        return;
      }
      if (request?.resource_type === 'contribution') {
        await contributionsAPI.update(requestId, {
          status: 'rejected',
          rejection_reason: reasonText || t('approvalRequests.defaultRejectionReason'),
        });
        success(t('approvalRequests.successContributionRejected'));
      } else {
        await approvalRequestsAPI.reject(requestId, reasonText);
        success(t('approvalRequests.successNotificationRead'));
      }
      loadData();
    } catch (err) {
      error(err.response?.data?.detail || t('approvalRequests.rejectError'));
    }
  };

  const loadWorkflowById = async (id) => {
    const wid = (id || workflowLookupId || '').trim();
    if (!wid) {
      error(t('approvalRequests.workflowIdRequired'));
      return;
    }
    try {
      setWorkflowLoading(true);
      const [d, h] = await Promise.all([
        workflowPlatformAPI.get(wid),
        workflowPlatformAPI.history(wid),
      ]);
      setWorkflowDetail(d.data);
      setWorkflowHistory(h.data?.history || []);
    } catch (err) {
      console.error(err);
      error(err.response?.data?.detail || t('approvalRequests.workflowLoadError'));
      setWorkflowDetail(null);
      setWorkflowHistory([]);
    } finally {
      setWorkflowLoading(false);
    }
  };

  const handleCreateWorkflow = async (e) => {
    e.preventDefault();
    if (!wfCreate.entity_id.trim()) {
      error(t('approvalRequests.entityIdRequired'));
      return;
    }
    try {
      setWorkflowLoading(true);
      const { data } = await workflowPlatformAPI.create({
        workflow_type: wfCreate.workflow_type,
        entity_id: wfCreate.entity_id.trim(),
        entity_type: wfCreate.entity_type.trim() || 'resource',
        initial_status: wfCreate.initial_status || 'draft',
      });
      success(t('approvalRequests.workflowCreated'));
      setWorkflowLookupId(data.id || '');
      setWorkflowDetail(data);
      const hist = await workflowPlatformAPI.history(data.id);
      setWorkflowHistory(hist.data?.history || []);
    } catch (err) {
      error(err.response?.data?.detail || t('approvalRequests.workflowCreateError'));
    } finally {
      setWorkflowLoading(false);
    }
  };

  const handleWorkflowApprove = async () => {
    if (!workflowDetail?.id) return;
    try {
      const comment = reasonInput.trim() || undefined;
      const { data } = await workflowPlatformAPI.approve(workflowDetail.id, comment);
      setWorkflowDetail(data);
      await loadWorkflowById(workflowDetail.id);
      success(t('approvalRequests.workflowApproved'));
    } catch (err) {
      error(err.response?.data?.detail || t('approvalRequests.workflowApproveError'));
    }
  };

  const handleWorkflowReject = async () => {
    if (!workflowDetail?.id) return;
    try {
      const comment = reasonInput.trim() || undefined;
      const { data } = await workflowPlatformAPI.reject(workflowDetail.id, comment);
      setWorkflowDetail(data);
      await loadWorkflowById(workflowDetail.id);
      success(t('approvalRequests.workflowRejected'));
    } catch (err) {
      error(err.response?.data?.detail || t('approvalRequests.workflowRejectError'));
    }
  };

  const formatRequestDateTime = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(localeFmt, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  };

  const getMemberName = (memberId) => {
    if (!memberId) return t('approvalRequests.notificationFallback');
    const normalizedId = String(memberId || '').trim();
    return (
      members[normalizedId]?.name ||
      t('approvalRequests.memberFallback', { id: normalizedId.substring(0, 8) })
    );
  };
  const getRequestPersonId = (request) =>
    String(
      request?.data?.person_id ||
      request?.data?.member_id ||
      request?.person_id ||
      request?.member_id ||
      request?.user_id ||
      request?.resource_id ||
      ''
    ).trim();

  const getActionLabel = (action) =>
    action === 'update' ? t('approvalRequests.actionUpdate') : t('approvalRequests.actionDelete');

  const submitReasonModal = async () => {
    const text = reasonInput.trim();
    if (reasonModal.required && !text) {
      error(t('approvalRequests.fieldRequired'));
      return;
    }

    try {
      setReasonSubmitting(true);
      if (reasonModal.mode === 'entrea_reject') {
        const target = requests.find((r) => String(r?.id || '').trim() === reasonModal.targetId);
        await handleReject(target, text || t('approvalRequests.defaultRejectionReason'));
      } else if (reasonModal.mode === 'workflow_approve') {
        await handleWorkflowApprove();
      } else if (reasonModal.mode === 'workflow_reject') {
        await handleWorkflowReject();
      }
      closeReasonModal();
    } catch (_err) {
      // Les handlers affichent déjà les erreurs toast.
    } finally {
      setReasonSubmitting(false);
    }
  };

  const filterRequests = () => {
    let filtered = requests;

    // Filtre par statut
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(r => r.status === selectedStatus);
    }

    // Recherche par nom du membre ou type d'action
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => {
        const memberName = getMemberName(getRequestPersonId(r)).toLowerCase();
        const actionLabel = getActionLabel(r.action).toLowerCase();
        const titleMsg = `${r.data?.title || ''} ${r.data?.message || ''}`.toLowerCase();
        return (
          memberName.includes(query) ||
          actionLabel.includes(query) ||
          titleMsg.includes(query)
        );
      });
    }

    setFilteredRequests(filtered);
  };

  const pageSubtitle = useMemo(() => {
    if (approvalTab === 'workflow') return t('approvalRequests.subtitleWorkflow');
    if (filteredRequests.length === 0 && requests.length > 0) {
      return t('approvalRequests.subtitleFilteredNone', { total: requests.length });
    }
    if (filteredRequests.length === 0) return t('approvalRequests.subtitleEmptyList');
    return t('approvalRequests.subtitleHasItems', { count: filteredRequests.length });
  }, [approvalTab, filteredRequests.length, requests.length, t]);

  if (!hasPermission('contributions.approve')) {
    return (
      <div className="space-y-3 sm:space-y-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <p className="text-red-600 text-sm sm:text-base">{t('approvalRequests.noPermission')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const requestsTotalPages = Math.max(1, Math.ceil(requestsTotal / itemsPerPage));
  const renderRequestsPagination = (placement) => (
    <Pagination
      placement={placement}
      currentPage={requestsPage}
      totalPages={requestsTotalPages}
      totalItems={requestsTotal}
      itemsPerPage={itemsPerPage}
      onPageChange={(page) => {
        setRequestsPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}
      onPageSizeChange={(n) => {
        setItemsPerPage(n);
        setRequestsPage(1);
      }}
    />
  );

  return (
    <div className="space-y-3 sm:space-y-4">
      {!embedded && (
        <PageHeader title={t('approvalRequests.pageTitle')} subtitle={pageSubtitle} />
      )}
      {embedded && (
        <p className="text-sm text-gray-600">{t('approvalRequests.embeddedHelp')}</p>
      )}

      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-3">
        <button
          type="button"
          onClick={() => setApprovalTab('entrea')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            approvalTab === 'entrea' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {embedded ? t('approvalRequests.tabAlerts') : t('approvalRequests.pageTitle')}
        </button>
        <button
          type="button"
          onClick={() => setApprovalTab('workflow')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            approvalTab === 'workflow' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {t('approvalRequests.tabWorkflow')}
        </button>
      </div>

      {approvalTab === 'workflow' && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 space-y-6">
          <p className="text-sm text-gray-600">{t('approvalRequests.workflowIntro')}</p>

          <form onSubmit={handleCreateWorkflow} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
            <h3 className="md:col-span-2 text-sm font-semibold text-gray-900">{t('approvalRequests.newWorkflow')}</h3>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('approvalRequests.labelType')}</label>
              <input
                className="w-full px-3 py-2 border rounded-lg text-sm"
                value={wfCreate.workflow_type}
                onChange={(e) => setWfCreate({ ...wfCreate, workflow_type: e.target.value })}
                placeholder={t('approvalRequests.placeholderWorkflowType')}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('approvalRequests.labelEntityId')}</label>
              <input
                className="w-full px-3 py-2 border rounded-lg text-sm"
                value={wfCreate.entity_id}
                onChange={(e) => setWfCreate({ ...wfCreate, entity_id: e.target.value })}
                placeholder={t('approvalRequests.placeholderEntityId')}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('approvalRequests.labelEntityType')}</label>
              <input
                className="w-full px-3 py-2 border rounded-lg text-sm"
                value={wfCreate.entity_type}
                onChange={(e) => setWfCreate({ ...wfCreate, entity_type: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('approvalRequests.labelInitialStatus')}</label>
              <input
                className="w-full px-3 py-2 border rounded-lg text-sm"
                value={wfCreate.initial_status}
                onChange={(e) => setWfCreate({ ...wfCreate, initial_status: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={workflowLoading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
              >
                {t('approvalRequests.createWorkflow')}
              </button>
            </div>
          </form>

          <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('approvalRequests.loadWorkflowById')}
              </label>
              <input
                className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                value={workflowLookupId}
                onChange={(e) => setWorkflowLookupId(e.target.value)}
                placeholder={t('approvalRequests.placeholderWorkflowUuid')}
              />
            </div>
            <button
              type="button"
              onClick={() => loadWorkflowById()}
              disabled={workflowLoading}
              className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm hover:bg-gray-900 disabled:opacity-50"
            >
              {t('approvalRequests.load')}
            </button>
          </div>

          {workflowLoading && !workflowDetail && (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
            </div>
          )}

          {workflowDetail && (
            <div className="border border-indigo-100 rounded-xl p-4 bg-indigo-50/40 space-y-3">
              <div className="flex flex-wrap gap-2 text-sm">
                <span className="font-semibold text-gray-900">ID</span>
                <code className="text-xs bg-white px-2 py-0.5 rounded border">{workflowDetail.id}</code>
                <span className="text-gray-600">
                  {t('approvalRequests.statusColon')}{' '}
                  {workflowDetail.status || t('approvalRequests.dash')}
                </span>
              </div>
              <pre className="text-xs bg-white p-3 rounded border overflow-x-auto max-h-48">
                {JSON.stringify(workflowDetail, null, 2)}
              </pre>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() =>
                    openReasonModal({
                      mode: 'workflow_approve',
                      targetId: workflowDetail?.id,
                      title: t('approvalRequests.wfApproveTitle'),
                      label: t('approvalRequests.wfApproveLabel'),
                      placeholder: t('approvalRequests.wfApprovePlaceholder'),
                    })
                  }
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm"
                >
                  {t('approvalRequests.approvePlatform')}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    openReasonModal({
                      mode: 'workflow_reject',
                      targetId: workflowDetail?.id,
                      title: t('approvalRequests.wfRejectTitle'),
                      label: t('approvalRequests.wfRejectLabel'),
                      placeholder: t('approvalRequests.wfRejectPlaceholder'),
                    })
                  }
                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm"
                >
                  {t('approvalRequests.rejectPlatform')}
                </button>
              </div>
              {workflowHistory.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">{t('approvalRequests.history')}</h4>
                  <ul className="text-xs space-y-1 text-gray-700 list-disc pl-5">
                    {workflowHistory.map((h, i) => (
                      <li key={i}>{typeof h === 'string' ? h : JSON.stringify(h)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {approvalTab === 'entrea' && (filteredRequests.length === 0 && requests.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8 text-center">
          <svg className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-2 text-sm sm:text-base font-medium text-gray-900">
            {t('approvalRequests.emptyPendingTitle')}
          </h3>
          <p className="mt-1 text-xs sm:text-sm text-gray-500">{t('approvalRequests.emptyPendingDesc')}</p>
        </div>
      ) : (
        <>
          {/* Barre de recherche et filtres */}
          {requests.length > 0 && (
            <SearchAndFilterBar
              searchValue={searchQuery}
              onSearchChange={setSearchQuery}
              searchInputClassName="min-h-[44px] md:min-h-0"
              collapseSearchOnMobile={embedded}
              mobileFiltersToggleLabel={embedded ? t('approvalRequests.filterStatusShort') : undefined}
              className={embedded ? 'max-md:!space-y-2 max-md:!p-2.5' : ''}
              placeholder={
                embedded ? t('approvalRequests.searchPlaceholderEmbedded') : t('approvalRequests.searchPlaceholderFull')
              }
              filters={[
                {
                  key: 'status',
                  label: t('approvalRequests.filterLabelStatus'),
                  type: 'button-group',
                  value: selectedStatus,
                  onChange: setSelectedStatus,
                  options: [
                    { value: 'all', label: t('approvalRequests.statusAll') },
                    { value: 'pending', label: t('approvalRequests.statusPending') },
                    { value: 'approved', label: t('approvalRequests.statusApproved') },
                    { value: 'rejected', label: t('approvalRequests.statusRejected') },
                  ],
                },
              ]}
            />
          )}
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            {renderRequestsPagination('top')}
          <div className="space-y-3 p-3">
            {filteredRequests.map((request, index) => {
            const cardColors = [
              'from-white via-amber-50/30 to-orange-50/30',
              'from-white via-yellow-50/30 to-amber-50/30',
              'from-white via-orange-50/30 to-red-50/30',
            ];
            return (
            <div key={request.id} className={`relative bg-gradient-to-br ${cardColors[index % cardColors.length]} rounded-2xl border-0 shadow-xl shadow-amber-500/10 p-5 backdrop-blur-sm overflow-hidden`}>
              {/* Decorative background circles */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 rounded-full opacity-10 blur-2xl"></div>
              <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-500 rounded-full opacity-10 blur-xl"></div>
              
              <div className="relative">
              <div className="flex items-start gap-3 mb-3">
                <span className={`px-4 py-2 rounded-full text-xs font-bold flex-shrink-0 shadow-lg ${
                  request.action === 'update' 
                    ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white' 
                    : 'bg-gradient-to-r from-red-500 to-rose-600 text-white'
                }`}>
                  {request.action === 'update'
                    ? t('approvalRequests.badgeUpdate')
                    : t('approvalRequests.badgeDelete')}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 mb-1">
                    {request.resource_type === 'notification' ? (
                      request.data?.title || t('approvalRequests.notificationFallback')
                    ) : request.resource_type === 'meeting' ? (
                      request.action === 'update'
                        ? t('approvalRequests.cardMeetingUpdate', {
                            title: request.data?.title || t('approvalRequests.minutesDefault'),
                          })
                        : t('approvalRequests.cardMeetingDelete', {
                            title: request.data?.title || t('approvalRequests.minutesDefault'),
                          })
                    ) : request.action === 'update' ? (
                      t('approvalRequests.cardContributionUpdate', {
                        name: getMemberName(getRequestPersonId(request)),
                      })
                    ) : (
                      t('approvalRequests.cardContributionDelete', {
                        name: getMemberName(getRequestPersonId(request)),
                      })
                    )}
                  </p>
                  {request.resource_type === 'notification' ? (
                    <p className="text-xs text-gray-500 mb-2">
                      {request.data?.message || request.reason || t('approvalRequests.notificationReceived')}
                    </p>
                  ) : (
                    request.reason && <p className="text-xs text-gray-500 mb-2">{request.reason}</p>
                  )}
                  {request.action === 'update' && request.data && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {request.data.amount && (
                        <span className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                          {t('approvalRequests.amountLabel', {
                            amount: Number(request.data.amount).toLocaleString(localeFmt),
                          })}
                        </span>
                      )}
                      {request.data.month && (
                        <span className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                          {t('approvalRequests.monthLabel', { month: request.data.month })}
                        </span>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-400">
                    {t('approvalRequests.requestedOn', {
                      datetime: formatRequestDateTime(request.created_at),
                    })}
                  </p>
                </div>
              </div>
              {request.status === 'pending' && (
                <div className="flex flex-col sm:flex-row gap-2 pt-3 border-t border-amber-100/50">
                  <button
                    onClick={() => handleApprove(request)}
                    className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 transition min-h-[44px] shadow-lg shadow-emerald-500/30"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {t('approvalRequests.approve')}
                  </button>
                  <button
                    onClick={() =>
                      openReasonModal({
                        mode: 'entrea_reject',
                        targetId: request.id,
                        title: t('approvalRequests.rejectModalTitle'),
                        label: t('approvalRequests.rejectModalLabel'),
                        placeholder: t('approvalRequests.rejectModalPlaceholder'),
                      })
                    }
                    className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 transition min-h-[44px] shadow-lg shadow-red-500/30"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    {t('approvalRequests.reject')}
                  </button>
                </div>
              )}
              {request.status === 'approved' && (
                <div className="pt-3 border-t border-amber-100/50">
                  <p className="text-sm text-green-600 font-medium">{t('approvalRequests.stateApproved')}</p>
                  {request.approved_at && (
                    <p className="text-xs text-gray-500 mt-1">
                      {t('approvalRequests.approvedOn', {
                        datetime: formatRequestDateTime(request.approved_at),
                      })}
                    </p>
                  )}
                </div>
              )}
              {request.status === 'rejected' && (
                <div className="pt-3 border-t border-amber-100/50">
                  <p className="text-sm text-red-600 font-medium">{t('approvalRequests.stateRejected')}</p>
                  {request.rejection_reason && (
                    <p className="text-xs text-gray-500 mt-1">{request.rejection_reason}</p>
                  )}
                </div>
              )}
              </div>
            </div>
            );
            })}
          </div>
            {renderRequestsPagination('bottom')}
          </div>
        </>
      ))}

      {reasonModal.open && (
        <ModalShell
          zClass="z-[100]"
          maxWidthClass="max-w-lg"
          onBackdropClick={closeReasonModal}
          panelClassName="overflow-hidden border-slate-200 p-0 flex flex-col"
        >
          <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-indigo-50/60">
              <h3 className="text-base sm:text-lg font-semibold text-slate-900">{reasonModal.title}</h3>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto flex-1 min-h-0">
              <label className="block text-sm font-medium text-slate-700">{reasonModal.label}</label>
              <textarea
                value={reasonInput}
                onChange={(e) => setReasonInput(e.target.value)}
                rows={4}
                placeholder={reasonModal.placeholder}
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="text-xs text-slate-500">
                {reasonModal.required
                  ? t('approvalRequests.modalFieldRequiredHint')
                  : t('approvalRequests.modalFieldOptionalHint')}
              </p>
            </div>
          <div className="px-5 py-4 border-t border-slate-100 bg-slate-50/90 backdrop-blur shrink-0 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                type="button"
                onClick={closeReasonModal}
                disabled={reasonSubmitting}
                className="px-4 py-2.5 min-h-[44px] rounded-xl text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-60"
              >
                {t('approvalRequests.cancel')}
              </button>
              <button
                type="button"
                onClick={submitReasonModal}
                disabled={reasonSubmitting}
                className="px-4 py-2.5 min-h-[44px] rounded-xl text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 disabled:opacity-60"
              >
                {reasonSubmitting ? t('approvalRequests.processing') : t('approvalRequests.confirm')}
              </button>
            </div>
        </ModalShell>
      )}
    </div>
  );
};

export default ApprovalRequests;

